import React from 'react'
import PropTypes from 'prop-types'
// material
import { styled } from '@mui/material/styles'
import MUIDataTable from 'mui-datatables'
import { Menu } from '@mui/material'
import { useTranslation } from 'react-i18next'

const StyledMUIDataTable = styled(MUIDataTable, {
  shouldForwardProp: (prop) => prop !== 'clickableRow',
})(({ clickableRow }) => ({
  ...(clickableRow && {
    '& .MuiTableRow-hover': {
      cursor: 'pointer',
    },
  }),
}))

BFTable.propTypes = {
  id: PropTypes.string,
  columns: PropTypes.array,
  data: PropTypes.object,
  page: PropTypes.number,
  sort: PropTypes.any,
  onRowClick: PropTypes.func,
  onChangePage: PropTypes.func,
  onColumnSortChange: PropTypes.func,
  pagination: PropTypes.bool,
  selectableRows: PropTypes.any,
  selectToolbarPlacement: PropTypes.string,
  rowsSelected: PropTypes.array,
  onRowSelectionChange: PropTypes.func,
  menuItems: PropTypes.array,
  onCellClick: PropTypes.func,
  customRowRender: PropTypes.func,
  customFooterRender: PropTypes.func,
  noDataMessage: PropTypes.string,
}

export default function BFTable({
  id,
  columns,
  data,
  page,
  sort,
  onRowClick,
  onChangePage,
  onColumnSortChange,
  pagination,
  selectableRows,
  selectToolbarPlacement,
  rowsSelected,
  onRowSelectionChange,
  menuItems,
  onCellClick,
  customRowRender,
  customFooterRender,
  noDataMessage,
}) {
  const { t } = useTranslation()

  const sortOrder = sort
    ? { name: sort.field, direction: sort.direction.toLowerCase() }
    : {}

  // Calculate actual item count excluding headers
  const actualItemCount = React.useMemo(() => {
    // If data has a separate count for non-header items, use it
    if (data && data.actualItemCount !== undefined) {
      return data.actualItemCount
    }
    // Otherwise use the itemCount as-is (for tables without headers)
    return data ? data.itemCount : 0
  }, [data])

  // Calculate actual items on current page (excluding headers)
  const actualRowsOnPage = React.useMemo(() => {
    if (data && data.items && Array.isArray(data.items)) {
      return data.items.filter((item) => !item.isHeader).length
    }
    return data ? data.items?.length || 0 : 0
  }, [data])

  // Calculate the start and end indices for current page display
  const { displayStart, displayEnd } = React.useMemo(() => {
    // Use backend-provided display range if available (dashboard tables only)
    if (
      data &&
      data.displayFrom !== undefined &&
      data.displayTo !== undefined &&
      data.actualItemCount !== undefined
    ) {
      return { displayStart: data.displayFrom, displayEnd: data.displayTo }
    }

    // Standard calculation for regular tables
    if (!data || actualItemCount === 0 || actualRowsOnPage === 0) {
      return { displayStart: 0, displayEnd: 0 }
    }

    // eslint-disable-next-line no-nested-ternary
    const actualRowsPerPage = data.range
      ? data.range.limit
      : data.items
      ? data.items.length
      : 10
    const actualRowsBeforePage = page * actualRowsPerPage
    const start = actualRowsBeforePage + 1
    const end = Math.min(
      actualRowsBeforePage + actualRowsOnPage,
      actualItemCount
    )

    return { displayStart: start, displayEnd: end }
  }, [page, data, actualItemCount, actualRowsOnPage])

  const [contextMenu, setContextMenu] = React.useState(null)

  const handleContextMenu = (event, rowIndex) => {
    event.preventDefault()
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX - 2,
            mouseY: event.clientY - 4,
            rowIndex,
          }
        : null
    )
  }

  const handleClose = () => {
    setContextMenu(null)
  }

  const createMenuItemComponents = (rowIndex) =>
    menuItems(rowIndex).map((component) => {
      const originalOnClick = component.props.onClick
      return React.cloneElement(component, {
        onClick: () => {
          handleClose()
          if (originalOnClick) {
            originalOnClick()
          }
        },
      })
    })

  return (
    data && (
      <>
        <Menu
          open={contextMenu !== null}
          onClose={handleClose}
          anchorReference="anchorPosition"
          disableAutoFocusItem
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
        >
          {contextMenu && createMenuItemComponents(contextMenu.rowIndex)}
        </Menu>
        <StyledMUIDataTable
          clickableRow={!!onRowClick}
          key={id}
          data={data.items}
          columns={columns}
          options={{
            enableNestedDataAccess: '.',
            page,
            filter: false,
            print: false,
            search: false,
            viewColumns: false,
            download: false,
            selectableRows: selectableRows || 'none',
            selectToolbarPlacement: selectToolbarPlacement || 'none',
            selectableRowsHeader:
              data && (data.itemCount || data.items?.length) > 0,
            rowsSelected,
            onRowSelectionChange,
            rowsPerPage: data.range ? data.range.limit : 10,
            rowsPerPageOptions: [],
            count: data.itemCount || actualItemCount,
            serverSide: true,
            sortOrder,
            onChangePage,
            onColumnSortChange,
            pagination: pagination !== false,
            onRowClick: onRowClick
              ? (rowData, rowMeta, event) => {
                  // Check if the click came from a button or its children
                  if (event && event.target) {
                    const { target } = event
                    // Check if clicked element or any parent is a button/icon
                    const clickedButton =
                      target.closest('button') ||
                      target.closest('svg') ||
                      target.closest('[role="button"]')
                    if (clickedButton) {
                      // Don't trigger row click if a button was clicked
                      return
                    }
                  }
                  onRowClick(rowData, rowMeta)
                }
              : undefined,
            onCellClick,
            customRowRender,
            customFooter: customFooterRender,
            setRowProps: menuItems
              ? (row, dataIndex) => ({
                  onContextMenu: (event) => handleContextMenu(event, dataIndex),
                })
              : undefined,
            textLabels: {
              body: {
                noMatch: noDataMessage ?? t('components.table.body.noMatch'),
                toolTip: t('components.table.body.toolTip'),
              },
              pagination: {
                next: t('components.table.pagination.next'),
                previous: t('components.table.pagination.previous'),
                rowsPerPage: t('components.table.pagination.rowsPerPage'),
                displayRows:
                  data &&
                  data.displayFrom !== undefined &&
                  data.displayTo !== undefined &&
                  data.actualItemCount !== undefined
                    ? `${displayStart}-${displayEnd} de ${actualItemCount}`
                    : t('components.table.pagination.displayRows'),
              },
              toolbar: {
                search: t('components.table.toolbar.search'),
                downloadCsv: t('components.table.toolbar.downloadCsv'),
                print: t('components.table.toolbar.print'),
                viewColumns: t('components.table.toolbar.viewColumns'),
                filterTable: t('components.table.toolbar.filterTable'),
              },
              filter: {
                all: t('components.table.filter.all'),
                title: t('components.table.filter.title'),
                reset: t('components.table.filter.reset'),
              },
              viewColumns: {
                title: t('components.table.viewColumns.title'),
                titleAria: t('components.table.viewColumns.titleAria'),
              },
              selectedRows: {
                text: t('components.table.selectedRows.text'),
                delete: t('components.table.selectedRows.delete'),
                deleteAria: t('components.table.selectedRows.deleteAria'),
              },
            },
          }}
        />
      </>
    )
  )
}
